import { getJeepRallyMediaUrl } from "../../../../../../api/jeep-rally/api";

export const REGISTRATION_STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "withdrawn", label: "Withdrawn" },
];

export const getRegistrationApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  const message = data?.message || error?.message || fallback;

  if (Array.isArray(data?.errors) && data.errors.length) {
    return `${message}: ${data.errors.join(", ")}`;
  }

  if (typeof data?.error === "string" && data.error) {
    return data.error;
  }

  return message;
};

export const normalizeRegistrations = (data) => {
  if (Array.isArray(data)) return data.filter(Boolean);
  if (Array.isArray(data?.data)) return data.data.filter(Boolean);
  if (Array.isArray(data?.registrations)) return data.registrations.filter(Boolean);
  return [];
};

export const normalizeRegistration = (data) => {
  if (!data) return null;
  if (Array.isArray(data)) return data[0] || null;
  if (data?.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    return data.data;
  }
  return data;
};

export const normalizeActiveRally = (data) => {
  if (!data) return null;
  if (Array.isArray(data)) return data[0] || null;
  if (data?.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    return data.data;
  }
  return data;
};

export const formatRegistrationDate = (dateValue) => {
  if (!dateValue) return "-";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

export const getCategoryId = (registration) => {
  const populated = getPopulatedCategory(registration);
  if (populated?._id || populated?.id) return populated._id || populated.id;
  if (typeof registration?.category_id === "string") {
    return registration.category_id;
  }
  return "";
};

export const getPopulatedCategory = (registration) => {
  const category = registration?.category_id;
  if (category && typeof category === "object") return category;
  if (registration?.category && typeof registration.category === "object") {
    return registration.category;
  }
  return null;
};

/**
 * Merge populated registration category with full category list so
 * consent_document / isSigned are available even when populate is partial.
 */
export const resolveInheritedCategory = (registration, categories = []) => {
  const populated = getPopulatedCategory(registration) || {};
  const categoryId =
    populated._id ||
    populated.id ||
    (typeof registration?.category_id === "string"
      ? registration.category_id
      : "");

  const fromList = categories.find(
    (item) => (item._id || item.id) === categoryId
  );

  if (!fromList && !Object.keys(populated).length) return null;

  return {
    ...(fromList || {}),
    ...populated,
    _id: categoryId || fromList?._id || fromList?.id || populated._id,
    consent: populated.consent ?? fromList?.consent ?? "",
    consent_document:
      populated.consent_document ??
      populated.consentDocument ??
      fromList?.consent_document ??
      fromList?.consentDocument ??
      "",
    isSigned:
      populated.isSigned ??
      populated.is_signed ??
      fromList?.isSigned ??
      fromList?.is_signed,
  };
};

export const isConsentContentEmpty = (html) => {
  const text = String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  return !text;
};

const looksLikeConsentFilePath = (value) =>
  Boolean(value) &&
  (/^https?:\/\//i.test(value) ||
    /^\/?uploads\//i.test(value) ||
    /\.(pdf|docx?|png|jpe?g|webp)(\?|#|$)/i.test(value));

export const getInheritedConsentInfo = (registration, categories = []) => {
  const category = resolveInheritedCategory(registration, categories);
  if (!category) {
    return {
      categoryId: "",
      category,
      templateHtml: "",
      templateUrl: "",
      hasConsentTemplate: false,
      hasBlankConsent: false,
      signedUrl: "",
      isSigned: false,
      downloadUrl: "",
    };
  }

  const consentRaw =
    typeof category.consent === "string" ? category.consent.trim() : "";
  const signedRaw =
    typeof category.consent_document === "string"
      ? category.consent_document.trim()
      : "";

  const isFilePath = looksLikeConsentFilePath(consentRaw);
  const templateHtml =
    isFilePath || isConsentContentEmpty(consentRaw) ? "" : consentRaw;
  const templateUrl =
    isFilePath && consentRaw ? getJeepRallyMediaUrl(consentRaw) : "";
  const signedUrl = signedRaw ? getJeepRallyMediaUrl(signedRaw) : "";
  const isSigned = category.isSigned === true || Boolean(signedRaw);
  const hasConsentTemplate = Boolean(templateHtml || templateUrl);

  return {
    categoryId: category._id || category.id || "",
    category,
    templateHtml,
    templateUrl,
    hasConsentTemplate,
    // Alias kept for existing UI call sites.
    hasBlankConsent: hasConsentTemplate,
    blankHtml: templateHtml,
    blankUrl: templateUrl,
    signedUrl,
    isSigned,
    downloadUrl: signedUrl || templateUrl,
  };
};

/** @deprecated Use getInheritedConsentInfo() */
export const getCategoryConsentUrl = (registration, categories = []) => {
  const info = getInheritedConsentInfo(registration, categories);
  return info.templateUrl || "";
};

export const openOrDownloadFile = (url, filename = "") => {
  if (!url) return false;

  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  if (filename) link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  return true;
};

export const sanitizeConsentFilenamePart = (value, fallback = "file") => {
  const cleaned = String(value || "")
    .trim()
    .replace(/[^\w.\-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
  return cleaned || fallback;
};

/**
 * Replace driver-name placeholders in a category consent template.
 * Supports {{driver_name}}, {{name}}, {{driverName}}, [driver_name], [name].
 */
export const personalizeConsentTemplate = (template, driverName) => {
  const name = String(driverName || "").trim();
  if (!name) return String(template || "");

  return String(template || "")
    .replace(/\{\{\s*driver_name\s*\}\}/gi, name)
    .replace(/\{\{\s*driverName\s*\}\}/gi, name)
    .replace(/\{\{\s*name\s*\}\}/gi, name)
    .replace(/\[\s*driver_name\s*\]/gi, name)
    .replace(/\[\s*name\s*\]/gi, name);
};

const buildConsentSignatureBlock = (driverName) => {
  const wrap = document.createElement("div");
  wrap.style.cssText = [
    "margin-top:64px",
    "display:flex",
    "justify-content:flex-end",
    "page-break-inside:avoid",
  ].join(";");

  const box = document.createElement("div");
  box.style.cssText = "width:280px;text-align:left;color:#111;";

  const sigLine = document.createElement("div");
  sigLine.style.cssText =
    "border-bottom:1px solid #111;height:56px;margin-bottom:6px;";

  const sigLabel = document.createElement("div");
  sigLabel.textContent = "Driver signature";
  sigLabel.style.cssText = "font-size:12px;margin-bottom:18px;";

  const dateLine = document.createElement("div");
  dateLine.style.cssText =
    "border-bottom:1px solid #111;height:28px;margin-bottom:6px;";

  const dateLabel = document.createElement("div");
  dateLabel.textContent = "Date";
  dateLabel.style.cssText = "font-size:12px;margin-bottom:12px;";

  const nameLabel = document.createElement("div");
  nameLabel.textContent = `Name: ${driverName || ""}`;
  nameLabel.style.cssText = "font-size:12px;";

  box.appendChild(sigLine);
  box.appendChild(sigLabel);
  box.appendChild(dateLine);
  box.appendChild(dateLabel);
  box.appendChild(nameLabel);
  wrap.appendChild(box);
  return wrap;
};

export const downloadConsentHtmlAsPdf = async ({
  html,
  filename = "consent-form.pdf",
  title = "Consent Form",
  driverName = "",
} = {}) => {
  if (isConsentContentEmpty(html)) return false;

  const html2canvas = (await import("html2canvas-pro")).default;
  const jsPdfModule = await import("jspdf");
  const jsPDF = jsPdfModule.default || jsPdfModule.jsPDF;

  const container = document.createElement("div");
  container.setAttribute("data-consent-pdf", "true");
  container.style.cssText = [
    "position:fixed",
    "left:-10000px",
    "top:0",
    "width:794px",
    "min-height:1123px",
    "padding:48px",
    "box-sizing:border-box",
    "background:#ffffff",
    "color:#111111",
    "font-family:Arial,Helvetica,sans-serif",
    "font-size:14px",
    "line-height:1.6",
    "display:flex",
    "flex-direction:column",
  ].join(";");

  const heading = document.createElement("h1");
  heading.textContent = title;
  heading.style.cssText =
    "margin:0 0 8px;font-size:22px;font-weight:700;color:#111;";

  if (driverName) {
    const driverLine = document.createElement("p");
    driverLine.textContent = `Driver: ${driverName}`;
    driverLine.style.cssText = "margin:0 0 24px;font-size:14px;color:#333;";
    container.appendChild(heading);
    container.appendChild(driverLine);
  } else {
    heading.style.marginBottom = "24px";
    container.appendChild(heading);
  }

  const body = document.createElement("div");
  body.innerHTML = html;
  body.style.cssText = "word-break:break-word;flex:1 1 auto;";

  container.appendChild(body);
  container.appendChild(buildConsentSignatureBlock(driverName));
  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const usableWidth = pageWidth - margin * 2;
    const imgHeight = (canvas.height * usableWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = margin;

    pdf.addImage(imgData, "PNG", margin, position, usableWidth, imgHeight);
    heightLeft -= pageHeight - margin * 2;

    while (heightLeft > 0) {
      position = margin - (imgHeight - heightLeft);
      pdf.addPage();
      pdf.addImage(imgData, "PNG", margin, position, usableWidth, imgHeight);
      heightLeft -= pageHeight - margin * 2;
    }

    const safeName = sanitizeConsentFilenamePart(filename, "consent-form.pdf");
    pdf.save(safeName.endsWith(".pdf") ? safeName : `${safeName}.pdf`);
    return true;
  } finally {
    container.remove();
  }
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const resolveConsentFilename = (registration) => {
  const driverName = getDriverName(registration);
  const teamName = getTeamName(registration);
  const categoryTitle = getCategoryTitle(registration);
  const suffix =
    teamName && teamName !== "-"
      ? teamName
      : categoryTitle && categoryTitle !== "-"
        ? categoryTitle
        : "consent";

  return `consent-${sanitizeConsentFilenamePart(
    driverName,
    "driver"
  )}-${sanitizeConsentFilenamePart(suffix, "form")}.pdf`;
};

/**
 * Personalize the category consent template with this driver's name and
 * download as PDF (includes signature block). Falls back to opening a
 * file URL when consent is stored as a path rather than HTML.
 */
export const downloadPersonalizedConsent = async (
  registration,
  categories = []
) => {
  const info = getInheritedConsentInfo(registration, categories);
  const driverName = getDriverName(registration);
  const hasDriverName = Boolean(driverName && driverName !== "-");

  if (!info.hasConsentTemplate) {
    return { ok: false, reason: "no_template" };
  }

  if (!hasDriverName) {
    return { ok: false, reason: "no_driver_name" };
  }

  const categoryTitle =
    info.category?.title || info.category?.key || "Consent Form";
  const filename = resolveConsentFilename(registration);
  const title = `${categoryTitle} — Consent Form`;

  if (info.templateHtml) {
    const personalized = personalizeConsentTemplate(
      info.templateHtml,
      driverName
    );
    const ok = await downloadConsentHtmlAsPdf({
      html: personalized,
      filename,
      title,
      driverName,
    });
    return ok
      ? { ok: true, reason: null }
      : { ok: false, reason: "generate_failed" };
  }

  if (info.templateUrl) {
    try {
      const response = await fetch(info.templateUrl);
      const contentType = response.headers.get("content-type") || "";
      const text = await response.text();
      const looksLikeHtml =
        /<\/?[a-z][\s\S]*>/i.test(text) ||
        contentType.includes("text/html") ||
        contentType.includes("text/plain");

      if (response.ok && looksLikeHtml && !isConsentContentEmpty(text)) {
        const personalized = personalizeConsentTemplate(text, driverName);
        const ok = await downloadConsentHtmlAsPdf({
          html: personalized,
          filename,
          title,
          driverName,
        });
        return ok
          ? { ok: true, reason: null }
          : { ok: false, reason: "generate_failed" };
      }
    } catch {
      // Fall through to raw file download.
    }

    openOrDownloadFile(info.templateUrl, filename);
    return { ok: true, reason: null };
  }

  return { ok: false, reason: "no_template" };
};

/** @deprecated Prefer downloadPersonalizedConsent() */
export const downloadBlankConsent = async (registration, categories = []) => {
  const result = await downloadPersonalizedConsent(registration, categories);
  return result.ok;
};

/**
 * Download a personalized consent PDF for each selected registration
 * (frontend-only; sequential to reduce popup blocking).
 */
export const downloadConsentFormsForRegistrations = async (
  registrations = [],
  categories = []
) => {
  let downloaded = 0;
  let skipped = 0;
  let skippedNoTemplate = 0;
  let skippedNoDriver = 0;

  for (let index = 0; index < registrations.length; index += 1) {
    const registration = registrations[index];
    const result = await downloadPersonalizedConsent(
      registration,
      categories
    );

    if (result.ok) {
      downloaded += 1;
    } else {
      skipped += 1;
      if (result.reason === "no_template") skippedNoTemplate += 1;
      if (result.reason === "no_driver_name") skippedNoDriver += 1;
    }

    if (index < registrations.length - 1) {
      await delay(450);
    }
  }

  return {
    downloaded,
    skipped,
    skippedNoTemplate,
    skippedNoDriver,
    total: registrations.length,
    // Backward-compatible alias used by older toasts.
    totalCategories: registrations.length,
  };
};

export const getCategoryTitle = (registration) => {
  const category = getPopulatedCategory(registration);
  if (category?.title) return category.title;
  if (category?.key) return category.key;
  if (typeof registration?.category_id === "string") {
    return registration.category_id;
  }
  return "-";
};

export const getPopulatedTeam = (registration) => {
  const team = registration?.team_id;
  if (team && typeof team === "object") return team;
  if (registration?.team && typeof registration.team === "object") {
    return registration.team;
  }
  return null;
};

export const getTeamName = (registration) => {
  const team = getPopulatedTeam(registration);
  return (
    team?.team_name ||
    team?.name ||
    registration?.team?.team_name ||
    registration?.team?.name ||
    "-"
  );
};

export const getTeamNumber = (registration) => {
  const team = getPopulatedTeam(registration);
  const number =
    team?.team_number ??
    team?.number ??
    registration?.team?.team_number ??
    registration?.team?.number;
  if (number === 0 || number) return String(number);
  return "-";
};

export const getDriverFromRegistration = (registration) => {
  const team = getPopulatedTeam(registration);
  const driver =
    team?.driver_id && typeof team.driver_id === "object"
      ? team.driver_id
      : null;

  if (driver) return driver;

  if (registration?.driver && typeof registration.driver === "object") {
    return registration.driver;
  }

  return null;
};

export const getDriverName = (registration) => {
  const driver = getDriverFromRegistration(registration);
  if (driver?.name) return driver.name;
  if (driver?.full_name) return driver.full_name;
  if (driver?.email) return driver.email;

  const team = getPopulatedTeam(registration);
  if (team?.driver_name) return team.driver_name;

  return "-";
};

export const getDriverEmail = (registration) => {
  const driver = getDriverFromRegistration(registration);
  return driver?.email || "-";
};

export const getDriverImageUrl = (registration) => {
  const driver = getDriverFromRegistration(registration);
  const path = driver?.profile_image || driver?.image || "";
  return path ? getJeepRallyMediaUrl(path) : "";
};

export const getNavigatorName = (registration) => {
  const team = getPopulatedTeam(registration);
  const navigator =
    team?.navigator_id && typeof team.navigator_id === "object"
      ? team.navigator_id
      : registration?.navigator;

  if (navigator && typeof navigator === "object") {
    return navigator.name || navigator.full_name || navigator.email || "-";
  }

  if (typeof navigator === "string" && navigator) return navigator;
  if (team?.navigator_name) return team.navigator_name;
  return "-";
};

export const getVehicleSummary = (registration) => {
  const vehicle =
    registration?.vehicle ||
    getPopulatedTeam(registration)?.vehicle ||
    null;

  if (!vehicle || typeof vehicle !== "object") return "-";

  const parts = [
    vehicle.make,
    vehicle.model,
    vehicle.year,
    vehicle.registration_number || vehicle.reg_number,
  ].filter(Boolean);

  return parts.length ? parts.join(" · ") : "-";
};

export const getStatusBadgeVariant = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "approved":
      return "success";
    case "pending":
      return "warning";
    case "rejected":
      return "danger";
    case "withdrawn":
      return "secondary";
    default:
      return "light";
  }
};

export const canApproveRegistration = (status) =>
  String(status || "").toLowerCase() === "pending";

export const canUnapproveRegistration = (status) =>
  String(status || "").toLowerCase() === "approved";
